import { useState } from 'react';
import { ChevronDown, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// Shared date-scope control for the Supplier Invoices, Client Invoices, and
// Bank Transactions pages (CLAUDE.md Commit 3.1). `value` is either null
// (all time) or { from, to } where `to` may be empty (open-ended from
// `from` onward). Server-side date_from/date_to params -- never a
// client-side row filter -- so X-Total-Count and the taxonomy/category
// chips stay in parity with the table (see services/invoice_query.py and
// routes/transactions.py).
export function DateScopeControl({ value, onChange, defaultFrom }) {
  const [draftFrom, setDraftFrom] = useState(value?.from || defaultFrom || '');
  const [draftTo, setDraftTo] = useState(value?.to || '');
  const [open, setOpen] = useState(false);

  const label = !value
    ? 'All time'
    : value.to
      ? `${value.from} → ${value.to}`
      : `From ${(value.from || '').slice(0, 4)}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1" data-testid="date-scope-trigger">
          <CalendarIcon className="h-3.5 w-3.5" />
          {label}
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3">
        <Button
          variant={!value ? 'default' : 'outline'}
          size="sm"
          className="w-full justify-start"
          data-testid="date-scope-all-time"
          onClick={() => { onChange(null); setOpen(false); }}
        >
          All time
        </Button>
        <div className="space-y-2 pt-1 border-t">
          <p className="text-xs text-muted-foreground pt-2">Custom range</p>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              data-testid="date-scope-from"
              className="text-xs"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              data-testid="date-scope-to"
              className="text-xs"
            />
          </div>
          <Button
            size="sm"
            className="w-full"
            disabled={!draftFrom && !draftTo}
            data-testid="date-scope-apply"
            onClick={() => { onChange({ from: draftFrom || undefined, to: draftTo || undefined }); setOpen(false); }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
